import { localDayDiff } from "./conversation-date.js";

const WEEKDAY_LOOKBACK_DAYS = 6;
const HEBREW_WEEKDAY_PREFIX = /^יום\s+/;

type Translate = (key: string) => string;

function parseDate(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** 24-hour clock time in the active locale, e.g. `13:42`. */
export function formatConversationClockTime(iso: string, locale: string): string {
  const date = parseDate(iso);
  if (!date) return "";
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/**
 * Conversation-list time: today shows the clock, yesterday a word, the past week a weekday,
 * this year a short day + month, and older dates a numeric date.
 */
export function formatConversationListTime(
  iso: string,
  locale: string,
  nowMs: number,
  t: Translate,
): string {
  const date = parseDate(iso);
  if (!date) return "";
  const diffDays = localDayDiff(new Date(nowMs), date);
  if (diffDays <= 0) return formatConversationClockTime(iso, locale);
  if (diffDays === 1) return t("inbox.date.yesterday");
  if (diffDays <= WEEKDAY_LOOKBACK_DAYS) {
    const hebrew = locale.toLowerCase().startsWith("he");
    const weekday = new Intl.DateTimeFormat(locale, { weekday: hebrew ? "long" : "short" }).format(date);
    return hebrew ? weekday.replace(HEBREW_WEEKDAY_PREFIX, "") : weekday;
  }
  if (date.getFullYear() === new Date(nowMs).getFullYear()) {
    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(date);
  }
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
}

/** Full date and time for tooltips, e.g. `Monday, 12 October 2026 at 13:42`. */
export function formatConversationFullDate(iso: string, locale: string): string {
  const date = parseDate(iso);
  if (!date) return "";
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/** `Today, 13:42` / `Yesterday, 13:42` / `Tomorrow, 13:42`, otherwise a short date with time. */
export function formatConversationRelativeDateTime(
  iso: string,
  locale: string,
  nowMs: number,
  t: Translate,
): string {
  const date = parseDate(iso);
  if (!date) return "";
  const now = new Date(nowMs);
  const diffDays = localDayDiff(now, date);
  const word =
    diffDays === 0
      ? t("inbox.date.today")
      : diffDays === 1
        ? t("inbox.date.yesterday")
        : diffDays === -1
          ? t("inbox.date.tomorrow")
          : null;
  if (word) return `${word}, ${formatConversationClockTime(iso, locale)}`;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== now.getFullYear() ? { year: "numeric" as const } : {}),
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}
